<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import ClientBondService from '@/services/ClientBondService';
import {generate2faCode, getLoggedUser} from '@/services/AuthService';
import OnboardingHeader from '@/components/layouts/OnboardingHeader.vue';
import BondPaymentInfo from '@/components/bonds/BondPaymentInfo.vue';
import {formatAmount, formatDate, formatNumber} from '@/helper';

/**
 * IMA-517 fáze 10b: nákup dluhopisu klientem – celoobrazovkový průvodce v rozložení ASO/SO (.app-popup).
 *
 * Kroky: 1. počet kusů (průběžný výpočet) → 2. shrnutí (dokumenty, souhlas) → 3. SMS kód → platební údaje
 * (bez čísla kroku, jako konec ASO). BE: BondPurchaseController (calculate / prepare / sign).
 *
 * Dostupnost se na BE ověřuje znovu při podpisu; když mezitím kusy dojdou nebo se změní dokumenty,
 * průvodce vrátí klienta na příslušný krok a SMS kód zůstává platný (BE ho při odmítnutí nespotřebuje).
 * V textech se nezmiňuje ESO – klient řeší jen nákup a platbu.
 */
const props = defineProps({
  // dluhopis z menu nebo detailu – stačí id, name a currency
  bond: {type: Object, required: true},
});
// close(purchased) – po nákupu rodič obnoví seznam / detail
const emit = defineEmits(['close']);

const {t, te} = useI18n();
const router = useRouter();

const STEPS = ['quantity', 'summary', 'sms'];
const step = ref('quantity');
const stepIndex = computed(() => STEPS.indexOf(step.value));

const quantity = ref(1);
const limits = ref(null);
const calculation = ref(null);
const documents = ref([]);
const frameworkContractState = ref(null);
const calculating = ref(false);

// výsledek prepare: pokyn a rámcová smlouva k podpisu
const prepared = ref(null);
const preparing = ref(false);
const agreed = ref(false);

// SMS kód po číslicích (políčko na každou číslici); kód pro BE je jejich spojení
const CODE_LENGTH = 6;
const codeDigits = ref(Array(CODE_LENGTH).fill(''));
const codeInputs = ref([]);
const verificationCode = computed(() => codeDigits.value.join(''));
const codeSent = ref(false);
const codeSending = ref(false);
const codeRequestInterval = ref(0);
const signing = ref(false);
const phone = ref(null);
const clientId = ref(null);

const payment = ref(null);
const purchased = ref(false);

// hláška k aktuálnímu kroku (alert-danger) a upozornění po návratu z podpisu (alert-warning)
const errorMessage = ref(null);
const warningMessage = ref(null);

let calculateTimer = null;
let calculateSequence = 0;
let pollTimer = null;
let codeTimer = null;
// načtení přihlášeného klienta – odeslání SMS kódu na něj počká (klient může přejít k podpisu dřív, než se načte)
let loggedUserLoaded = null;

onMounted(() => {
  loggedUserLoaded = getLoggedUser().then(user => {
    phone.value = user.phone;
    clientId.value = user.client?.id ?? null;
  }).catch(() => {});
  calculate();
});

onBeforeUnmount(() => {
  clearTimeout(calculateTimer);
  clearTimeout(pollTimer);
  clearInterval(codeTimer);
});

/** Nejvyšší počet kusů, který lze koupit: min(volné kusy, max na nákup); null = bez omezení. */
const maxQuantity = computed(() => limits.value?.can_buy ?? null);
const minQuantity = computed(() => limits.value?.min_quantity ?? 1);
/** Nákup nejde, když volných kusů je méně než minimum na nákup. */
const soldOut = computed(() => maxQuantity.value !== null && maxQuantity.value < minQuantity.value);
const quantityValid = computed(() => Number.isInteger(quantity.value)
    && quantity.value >= minQuantity.value
    && (maxQuantity.value === null || quantity.value <= maxQuantity.value));

/** Dokumenty k podpisu se ještě generují – podepsat půjde, až budou všechny hotové. */
const documentsGenerating = computed(() => (prepared.value?.documents_to_sign ?? []).some(document => document.is_generating || !document.file));
const hasFrameworkDocuments = computed(() => (prepared.value?.documents_to_sign ?? []).some(document => document.kind === 'FRAMEWORK_CONTRACT'));

/** Přepočet ceny se zpožděním, aby se při klikání na stepper neposílal požadavek za každý klik. */
watch(quantity, () => {
  clearTimeout(calculateTimer);
  if (!quantityValid.value) {
    return;
  }
  calculateTimer = setTimeout(calculate, 400);
});

/**
 * Krok 1: cena, limity a dokumenty k emisi. Odpověď staršího požadavku se zahodí (pořadové číslo),
 * aby pomalá odpověď nepřepsala výpočet pro novější počet kusů.
 */
function calculate() {
  const sequence = ++calculateSequence;
  calculating.value = true;
  ClientBondService.calculatePurchase(props.bond.id, quantity.value).then(response => {
    if (sequence !== calculateSequence) {
      return;
    }
    limits.value = response.limits;
    calculation.value = response.calculation;
    documents.value = response.documents;
    frameworkContractState.value = response.framework_contract_state;
    errorMessage.value = null;
  }).catch(error => {
    if (sequence !== calculateSequence) {
      return;
    }
    handleQuantityError(error);
  }).finally(() => {
    if (sequence === calculateSequence) {
      calculating.value = false;
    }
  });
}

/**
 * Chyby počtu kusů (výpočet i podpis): BE vrací nové limity – počet se sníží na to, co lze koupit,
 * a výpočet se zopakuje (watch na quantity).
 */
function handleQuantityError(error) {
  const data = error.data ?? {};
  // první načtení s výchozím 1 ks u dluhopisu s vyšším minimem – nejde o chybu klienta, jen se nastaví minimum
  const isFirstLoad = !limits.value;
  if (data.limits) {
    limits.value = data.limits;
    if (!soldOut.value && quantity.value < minQuantity.value) {
      quantity.value = minQuantity.value;
    } else if (!soldOut.value && maxQuantity.value !== null && quantity.value > maxQuantity.value) {
      quantity.value = maxQuantity.value;
    }
  }
  if (isFirstLoad && data.code === 'INVALID_QUANTITY') {
    errorMessage.value = null;
  } else if (data.code === 'NOT_ENOUGH_PIECES') {
    errorMessage.value = t('bondPurchase.errors.NOT_ENOUGH_PIECES', {available: formatNumber(data.available ?? 0, 0)});
  } else if (data.code === 'INVALID_QUANTITY') {
    errorMessage.value = t('bondPurchase.errors.INVALID_QUANTITY');
  } else if (error.status === 404) {
    errorMessage.value = t('bondPurchase.errors.BOND_NOT_AVAILABLE');
  } else {
    errorMessage.value = data.message ?? t('bondPurchase.errors.general');
  }
}

function changeQuantity(delta) {
  const next = (quantity.value || 0) + delta;
  if (next < minQuantity.value || (maxQuantity.value !== null && next > maxQuantity.value)) {
    return;
  }
  quantity.value = next;
}

/** Krok 1 → 2: připraví pokyn a rámcovou smlouvu k podpisu. */
function prepare() {
  preparing.value = true;
  errorMessage.value = null;
  ClientBondService.preparePurchase(props.bond.id, quantity.value).then(response => {
    prepared.value = response;
    calculation.value = response.calculation;
    documents.value = response.documents;
    frameworkContractState.value = response.framework_contract_state;
    agreed.value = false;
    step.value = 'summary';
    pollDocuments();
  }).catch(error => {
    step.value = 'quantity';
    handleQuantityError(error);
  }).finally(() => {
    preparing.value = false;
  });
}

/**
 * Pokyn a rámcová smlouva se generují ve frontě – dokud nemají soubor, ptáme se po 2 s znovu.
 * Po minutě se dotazování zastaví; klient se může vrátit a nákup připravit znovu.
 */
function pollDocuments(attempt = 0) {
  clearTimeout(pollTimer);
  if (!documentsGenerating.value || attempt >= 30) {
    return;
  }
  pollTimer = setTimeout(() => {
    const pending = prepared.value.documents_to_sign.filter(document => document.is_generating || !document.file);
    Promise.all(pending.map(document => ClientBondService.getPurchaseDocument(props.bond.id, document.id)
        .then(fresh => Object.assign(document, fresh))
        .catch(() => null)))
        .finally(() => pollDocuments(attempt + 1));
  }, 2000);
}

/** Krok 2 → 3: odešle SMS kód (při návratu po chybě se už odeslaný kód nepřeposílá). */
function goToSms() {
  step.value = 'sms';
  errorMessage.value = null;
  if (!codeSent.value) {
    sendCode();
  }
}

/** SMS kód pro ověřovaný objekt CLIENT – BE ho při podpisu ověřuje proti klientovi (BondPurchaseService::sign). */
async function sendCode() {
  await loggedUserLoaded;
  if (!clientId.value) {
    errorMessage.value = t('bondPurchase.errors.general');
    return;
  }
  // BE vyžaduje příjemce SMS – telefon z profilu přihlášeného klienta (stejně jako v onboardingu)
  if (!phone.value) {
    errorMessage.value = t('bondPurchase.errors.no_phone');
    return;
  }
  codeSending.value = true;
  errorMessage.value = null;
  generate2faCode({verifiable_type: 'CLIENT', verifiable_id: clientId.value, service: 'SMS', recipient: phone.value}, 'api/verification-code/generate').then(() => {
    codeSent.value = true;
    startCodeInterval();
  }).catch(error => {
    errorMessage.value = error?.message ?? t('bondPurchase.errors.general');
  }).finally(() => {
    codeSending.value = false;
  });
}

/**
 * Telefon pro text o odeslání kódu ve tvaru „+420 *** *** 512“: předvolba a poslední tři číslice,
 * zbytek po trojicích hvězdiček.
 */
const maskedPhone = computed(() => {
  const value = (phone.value ?? '').replace(/\s+/g, '');
  if (value.length < 6) {
    return value;
  }
  const prefix = value.startsWith('+') ? value.slice(0, 4) : '';
  const rest = value.slice(prefix.length);
  const hidden = Array(Math.ceil((rest.length - 3) / 3)).fill('***');
  return [prefix, ...hidden, rest.slice(-3)].filter(Boolean).join(' ');
});

/** Do políčka jde jen jedna číslice; po jejím zadání se skočí do dalšího. Vložení celého kódu se rozdělí do políček. */
function onCodeInput(index, event) {
  const digits = event.target.value.replace(/\D/g, '');
  if (digits.length > 1) {
    digits.slice(0, CODE_LENGTH - index).split('').forEach((digit, offset) => {
      codeDigits.value[index + offset] = digit;
    });
    focusCode(Math.min(index + digits.length, CODE_LENGTH - 1));
    return;
  }
  codeDigits.value[index] = digits;
  event.target.value = digits;
  if (digits && index < CODE_LENGTH - 1) {
    focusCode(index + 1);
  }
}

/** Backspace v prázdném políčku vrátí kurzor do předchozího. */
function onCodeKeydown(index, event) {
  if (event.key === 'Backspace' && !codeDigits.value[index] && index > 0) {
    focusCode(index - 1);
  }
}

function focusCode(index) {
  codeInputs.value[index]?.focus();
  codeInputs.value[index]?.select();
}

/** Opětovné odeslání kódu jde až po 30 s (stejně jako v ASO). */
function startCodeInterval() {
  codeRequestInterval.value = 30;
  clearInterval(codeTimer);
  codeTimer = setInterval(() => {
    codeRequestInterval.value--;
    if (codeRequestInterval.value <= 0) {
      clearInterval(codeTimer);
    }
  }, 1000);
}

/**
 * Krok 3: podpis. Odsouhlasené verze dokumentů k emisi = file.id ze shrnutí; když se mezitím změnily,
 * BE vrátí DOCUMENTS_CHANGED a klient je musí odsouhlasit znovu.
 */
function sign() {
  signing.value = true;
  errorMessage.value = null;
  warningMessage.value = null;
  ClientBondService.signPurchase(props.bond.id, {
    quantity: quantity.value,
    verification_code: verificationCode.value,
    accepted_media_ids: documents.value.map(document => document.file.id),
    purchase_order_document_id: prepared.value?.purchase_order_document_id ?? null,
  }).then(response => {
    payment.value = response.payment;
    purchased.value = true;
    step.value = 'payment';
  }).catch(handleSignError).finally(() => {
    signing.value = false;
  });
}

/** Reakce na odmítnutý podpis – návrat na krok, kde klient může problém vyřešit. */
function handleSignError(error) {
  const data = error.data ?? {};
  switch (data.code) {
    case 'INVALID_VERIFICATION_CODE':
      errorMessage.value = te(data.message) ? t(data.message) : t('bondPurchase.errors.INVALID_VERIFICATION_CODE');
      break;
    case 'DOCUMENTS_CHANGED':
      documents.value = data.documents ?? [];
      agreed.value = false;
      step.value = 'summary';
      warningMessage.value = t('bondPurchase.errors.DOCUMENTS_CHANGED');
      break;
    case 'PURCHASE_ORDER_MISMATCH':
      warningMessage.value = t('bondPurchase.errors.PURCHASE_ORDER_MISMATCH');
      prepare();
      break;
    case 'NOT_ENOUGH_PIECES':
    case 'INVALID_QUANTITY':
      step.value = 'quantity';
      prepared.value = null;
      handleQuantityError(error);
      break;
    case 'NO_CONTRACT':
      errorMessage.value = t('bondPurchase.errors.NO_CONTRACT');
      break;
    default:
      errorMessage.value = error.status === 404 ? t('bondPurchase.errors.BOND_NOT_AVAILABLE') : (data.message ?? t('bondPurchase.errors.general'));
  }
}

function back() {
  errorMessage.value = null;
  warningMessage.value = null;
  if (step.value === 'sms') {
    step.value = 'summary';
  } else if (step.value === 'summary') {
    step.value = 'quantity';
    clearTimeout(pollTimer);
  }
}

function close() {
  emit('close', purchased.value);
}

/**
 * Dokumenty do souhlasu v rekapitulaci: nejdřív k podpisu (pokyn k nákupu, rámcová smlouva), pak dokumenty k emisi
 * s verzí. Odkaz vede na soubor; dokument, který se ještě generuje, odkaz nemá (dotazování v pollDocuments).
 */
const agreementDocuments = computed(() => [
  ...(prepared.value?.documents_to_sign ?? []).map(document => ({
    key: 'sign-' + document.id,
    name: document.name,
    url: previewUrl(document.file),
    version: null,
  })),
  ...documents.value.map(document => ({
    key: 'bond-' + document.id,
    name: document.name,
    url: previewUrl(document.file),
    version: document.version,
  })),
]);

/**
 * Odkaz na náhled dokumentu v prohlížeči PDF aplikace (stránka PDFViewer). Otevírá se v nové záložce, aby se
 * neztratil rozpracovaný nákup; download_url by soubor jen stáhl. Bez souboru (ještě se generuje) null.
 */
function previewUrl(file) {
  return file?.uuid ? router.resolve({name: 'PDFViewer', params: {uuid: file.uuid}}).href : null;
}

/** Řádek pod názvem dluhopisu: cena za kus · úrok · výplata · konec prodeje. */
const bondHeadline = computed(() => [
  props.bond.nominal_value !== null && props.bond.nominal_value !== undefined ? formatAmount(props.bond.nominal_value, props.bond.currency) + ' / ' + t('bondPurchase.pieces') : null,
  props.bond.coupon_rate !== null && props.bond.coupon_rate !== undefined ? formatNumber(props.bond.coupon_rate) + ' % p.a.' : null,
  props.bond.coupon_frequency_label ? t('bondPurchase.payout', {frequency: props.bond.coupon_frequency_label}) : null,
  props.bond.sale_to ? t('bondPurchase.sale_to', {date: formatDate(props.bond.sale_to, 'd.m.Y')}) : null,
].filter(Boolean).join(' · '));

/** „Minimálně X ks, maximálně Y ks na nákup.“ – bez maxima jen minimum. */
const limitsText = computed(() => (props.bond && limits.value?.max_quantity
  ? t('bondPurchase.limits_min_max', {min: formatNumber(minQuantity.value, 0), max: formatNumber(limits.value.max_quantity, 0)})
  : t('bondPurchase.limits_min', {min: formatNumber(minQuantity.value, 0)})));

/** Text u stepperu „Můžete zakoupit xy ks.“ – bez omezení se nezobrazuje. */
const canBuyText = computed(() => maxQuantity.value === null ? null : t('bondPurchase.can_buy', {count: formatNumber(maxQuantity.value, 0)}));
</script>

<template>
  <div class="app-popup">
    <OnboardingHeader></OnboardingHeader>
    <!-- animované kruhy po stranách jako v onboardingu (ClientOnboardingOverlayNew) -->
    <div class="circles-container-wrapper">
      <div class="circles-container" style="top: 310px; right: -220px;">
        <div class="circles__img"></div>
      </div>
      <div class="circles-container" style="left: -130px; top: 20px;">
        <div class="circles__img"></div>
      </div>
    </div>
    <div class="row h-100 overflow-auto justify-content-center px-0 py-5 px-lg-5 mx-0" style="z-index: 1055">
      <div class="col-md-8 mb-5">
        <div class="d-flex justify-content-end">
          <i class="btn-close cursor-pointer" @click="close" :aria-label="t('bondPurchase.close')"></i>
        </div>
        <!-- titulek kroku jako ve vzoru („Kolik kusů chcete koupit?“, „Rekapitulace a dokumenty“, „Podpis pokynu“) -->
        <div v-if="step !== 'payment'" class="app-popup__title">{{ t('bondPurchase.step_titles.' + step) }}</div>
        <div class="app-popup__content">
          <div class="card">
            <div v-if="step !== 'payment'" class="card-header border-bottom p-3">
              <div class="col-12 mb-2 d-flex justify-content-between">
                <small>{{ t('bondPurchase.step') }} {{ stepIndex + 1 }} / {{ STEPS.length }}</small>
                <small>{{ t('bondPurchase.steps.' + step) }}</small>
              </div>
              <div class="progress">
                <div class="progress-bar" role="progressbar" :style="{width: (((stepIndex + 1) / STEPS.length) * 100) + '%'}"></div>
              </div>
            </div>
            <div class="card-body">
              <div v-if="warningMessage" class="alert alert-warning">{{ warningMessage }}</div>

              <!-- 1. počet kusů: dluhopis, dostupnost, počet kusů, cena -->
              <template v-if="step === 'quantity'">
                <div class="mb-3">
                  <div class="bond-purchase__title">{{ bond.name }}</div>
                  <small class="text-muted">{{ bondHeadline }}</small>
                </div>
                <div v-if="!limits && calculating" class="text-center my-4">
                  <div class="spinner-border text-primary" role="status"><span class="visually-hidden">{{ t('clientBonds.loading') }}</span></div>
                </div>
                <template v-else-if="limits">
                  <div v-if="soldOut" class="alert alert-info">{{ t('bondPurchase.sold_out') }}</div>
                  <template v-else>
                    <!-- volné kusy z ESO; bez objemu z ESO se dostupnost neomezuje -->
                    <div class="alert alert-success">
                      <template v-if="limits.available !== null">{{ t('bondPurchase.availability') }} <strong>{{ t('bondPurchase.availability_pieces', {count: formatNumber(limits.available, 0)}) }}</strong></template>
                      <template v-else>{{ t('bondPurchase.availability_unlimited') }}</template>
                    </div>
                    <label class="form-label fw-bold" for="bond-purchase-quantity">{{ t('bondPurchase.quantity') }}</label>
                    <!-- samostatná tlačítka − / + kolem pole s počtem (podle vzoru) -->
                    <div class="bond-purchase__stepper mb-2">
                      <button type="button" :disabled="quantity <= minQuantity" :aria-label="t('bondPurchase.decrease')" @click="changeQuantity(-1)">−</button>
                      <input id="bond-purchase-quantity" v-model.number="quantity" type="number" inputmode="numeric"
                             :min="minQuantity" :max="maxQuantity ?? undefined" step="1">
                      <button type="button" :disabled="maxQuantity !== null && quantity >= maxQuantity" :aria-label="t('bondPurchase.increase')" @click="changeQuantity(1)">+</button>
                    </div>
                    <small class="text-muted d-block">{{ limitsText }}</small>
                    <div v-if="!quantityValid" class="text-danger small mt-1">{{ t('bondPurchase.errors.INVALID_QUANTITY') }}</div>
                  </template>

                  <!-- cena: nominál, AÚV k datu výpočtu, poplatek a celkem k úhradě -->
                  <div v-if="calculation && !soldOut" class="card bg-light mt-4" :class="{'opacity-50': calculating}">
                    <div class="card-body">
                      <dl class="row mb-0">
                        <dt class="col-6 col-md-4 fw-normal text-muted">{{ t('bondPurchase.nominal_amount') }}</dt>
                        <dd class="col-6 col-md-8 mb-1">{{ formatAmount(calculation.nominal_amount, calculation.currency) }}</dd>
                        <dt class="col-6 col-md-4 fw-normal text-muted">{{ t('bondPurchase.accrued_interest_at', {date: formatDate(calculation.calculation_date, 'd.m.Y')}) }}</dt>
                        <dd class="col-6 col-md-8 mb-1">{{ formatAmount(calculation.accrued_interest, calculation.currency) }}</dd>
                        <dt class="col-6 col-md-4 fw-normal text-muted">{{ t('bondPurchase.fee') }}</dt>
                        <dd class="col-6 col-md-8 mb-1">{{ formatAmount(calculation.fee_amount, calculation.currency) }}</dd>
                        <dt class="col-6 col-md-4 fs-5">{{ t('bondPurchase.total') }}</dt>
                        <dd class="col-6 col-md-8 mb-0 fs-5 fw-bold">{{ formatAmount(calculation.total_amount, calculation.currency) }}</dd>
                      </dl>
                    </div>
                  </div>
                  <small v-if="calculation && !soldOut" class="text-muted d-block mt-3">{{ t('bondPurchase.accrued_note') }}</small>
                </template>
                <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
                <div class="d-flex justify-content-between mt-4">
                  <button type="button" class="btn btn-md ps-0" @click="close"><span class="arrow-left-icon"></span><span>{{ t('bondPurchase.back_to_app') }}</span></button>
                  <button class="btn btn-primary btn-md" type="button" :disabled="!limits || soldOut || !quantityValid || calculating || preparing" @click="prepare">
                    <span v-if="preparing" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <span>{{ t('bondPurchase.continue') }}</span>
                    <span class="arrow-right-icon"></span>
                  </button>
                </div>
              </template>

              <!-- 2. shrnutí -->
              <template v-if="step === 'summary' && prepared">
                <div class="mb-3">
                  <div class="bond-purchase__title">{{ bond.name }}</div>
                  <small class="text-muted">{{ bondHeadline }}</small>
                </div>
                <!-- rekapitulace nákupu ve stejném rámečku jako cena v prvním kroku -->
                <div class="card bg-light mb-4">
                  <div class="card-body">
                    <dl class="row mb-0">
                      <dt class="col-6 col-md-4 fw-normal text-muted">{{ t('bondPurchase.quantity') }}</dt>
                      <dd class="col-6 col-md-8 mb-1">{{ formatNumber(calculation.quantity, 0) }} {{ t('bondPurchase.pieces') }}</dd>
                      <dt class="col-6 col-md-4 fw-normal text-muted">{{ t('bondPurchase.nominal_amount') }}</dt>
                      <dd class="col-6 col-md-8 mb-1">{{ formatAmount(calculation.nominal_amount, calculation.currency) }}</dd>
                      <dt class="col-6 col-md-4 fw-normal text-muted">{{ t('bondPurchase.accrued_short') }}</dt>
                      <dd class="col-6 col-md-8 mb-1">{{ formatAmount(calculation.accrued_interest, calculation.currency) }}</dd>
                      <dt v-if="Number(calculation.fee_amount)" class="col-6 col-md-4 fw-normal text-muted">{{ t('bondPurchase.fee') }}</dt>
                      <dd v-if="Number(calculation.fee_amount)" class="col-6 col-md-8 mb-1">{{ formatAmount(calculation.fee_amount, calculation.currency) }}</dd>
                      <dt class="col-6 col-md-4 fs-5">{{ t('bondPurchase.total_short') }}</dt>
                      <dd class="col-6 col-md-8 mb-0 fs-5 fw-bold">{{ formatAmount(calculation.total_amount, calculation.currency) }}</dd>
                    </dl>
                  </div>
                </div>

                <!-- rámcová smlouva: podepíše se s nákupem; bez dokumentů k podpisu (MISSING bez typu smlouvy) jen informace -->
                <div v-if="hasFrameworkDocuments" class="alert alert-info">{{ t('bondPurchase.framework_sign_together') }}</div>
                <div v-else-if="frameworkContractState && frameworkContractState !== 'SIGNED'" class="alert alert-info">{{ t('bondPurchase.framework_missing') }}</div>

                <!-- souhlas s připojenými dokumenty jako ve Shrnutí onboardingu: dokumenty k podpisu (pokyn, rámcová smlouva)
                     a dokumenty k emisi s verzí, každý jako odkaz na soubor; generovaný dokument se zatím jen ohlásí -->
                <div class="form-check mt-2">
                  <input id="bond-purchase-agree" v-model="agreed" class="form-check-input" type="checkbox">
                  <label class="form-check-label" for="bond-purchase-agree">
                    <template v-if="agreementDocuments.length">
                      {{ t('bondPurchase.agree_with') }}
                      <span v-for="(document, index) of agreementDocuments" :key="document.key">
                        <a v-if="document.url" :href="document.url" target="_blank" class="text-decoration-underline">{{ document.name }}</a>
                        <span v-else>{{ document.name }} <small class="text-muted"><span class="spinner-border spinner-border-sm" role="status"></span> {{ t('bondPurchase.document_generating') }}</small></span>
                        <small v-if="document.version" class="text-muted"> ({{ t('bondPurchase.version', {version: document.version}) }})</small>{{ index < agreementDocuments.length - 1 ? ', ' : '.' }}
                      </span>
                    </template>
                    <template v-else>{{ t('bondPurchase.agree_without_documents') }}</template>
                  </label>
                </div>
                <small class="text-muted d-block mt-3">{{ t('bondPurchase.documents_note') }}</small>
                <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
                <div class="d-flex justify-content-between mt-4">
                  <button type="button" class="btn btn-md ps-0" @click="back"><span class="arrow-left-icon"></span><span>{{ t('bondPurchase.back') }}</span></button>
                  <button class="btn btn-primary btn-md" type="button" :disabled="!agreed || documentsGenerating || preparing" @click="goToSms">
                    <span>{{ t('bondPurchase.sign') }}</span>
                    <span class="arrow-right-icon"></span>
                  </button>
                </div>
              </template>

              <!-- 3. SMS kód -->
              <template v-if="step === 'sms'">
                <form @submit.prevent="sign">
                  <div class="form-group">
                    <p class="mb-3">{{ t(hasFrameworkDocuments ? 'bondPurchase.sms_text_framework' : 'bondPurchase.sms_text', {phone: maskedPhone}) }}</p>
                    <!-- políčko na každou číslici kódu -->
                    <div class="bond-purchase__code mb-2">
                      <input v-for="(digit, index) of codeDigits" :key="index" :ref="el => { codeInputs[index] = el; }"
                             :value="digit" type="text" inputmode="numeric" maxlength="1" :autocomplete="index === 0 ? 'one-time-code' : 'off'"
                             :aria-label="t('bondPurchase.code_digit', {number: index + 1})"
                             @input="onCodeInput(index, $event)" @keydown="onCodeKeydown(index, $event)" @focus="$event.target.select()">
                    </div>
                    <div class="mt-2">
                      <span v-if="codeSending" class="spinner-border spinner-border-sm" role="status"></span>
                      <a v-else-if="codeRequestInterval <= 0" class="cursor-pointer" @click="sendCode"><small>{{ codeSent ? t('bondPurchase.send_code_again') : t('bondPurchase.send_code') }}</small></a>
                      <small v-else class="text-muted">{{ t('bondPurchase.code_resend_in', {seconds: codeRequestInterval}) }}</small>
                    </div>
                  </div>
                  <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
                  <div class="d-flex justify-content-between mt-4">
                    <button type="button" class="btn btn-md ps-0" @click="back"><span class="arrow-left-icon"></span><span>{{ t('bondPurchase.back') }}</span></button>
                    <button class="btn btn-primary btn-md" type="submit" :disabled="verificationCode.length < CODE_LENGTH || signing">
                      <span v-if="signing" class="spinner-border spinner-border-sm me-1" role="status"></span>
                      <span>{{ t('bondPurchase.confirm_purchase') }}</span>
                      <span class="arrow-right-icon"></span>
                    </button>
                  </div>
                </form>
              </template>

              <!-- platební údaje po podpisu (bez čísla kroku, jako konec ASO) -->
              <template v-if="step === 'payment'">
                <h3 class="text-center mt-2 mb-2">{{ t('bondPurchase.done_title') }}</h3>
                <p class="text-center text-muted mb-4">{{ t('bondPurchase.done_text') }}</p>
                <BondPaymentInfo v-if="payment" :payment="payment"/>
                <div class="text-center mt-4">
                  <button class="btn btn-primary btn-md" type="button" @click="close">{{ t('bondPurchase.finish') }}</button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* šipky v tlačítkách Zpět / Pokračovat – stejné jako v onboardingu (ClientOnboardingOverlayNew) */
.arrow-right-icon {
  margin-left: 5px;
  line-height: 18px;
  font-size: 18px;
}

.arrow-right-icon::before {
  content: "\f0054";
  font: normal normal normal 18px / 1 "Material Design Icons";
}

.arrow-left-icon {
  margin-right: 5px;
  line-height: 18px;
  font-size: 18px;
}

.arrow-left-icon::before {
  content: "\F004D";
  font: normal normal normal 18px / 1 "Material Design Icons";
}

/* SMS kód: políčko na každou číslici */
.bond-purchase__code {
  display: flex;
  gap: .5rem;
}

.bond-purchase__code input {
  width: 3rem;
  height: 3.25rem;
  border: 1px solid #ced4da;
  border-radius: .375rem;
  text-align: center;
  font-size: 1.5rem;
}

/* název dluhopisu v průvodci – větší a tučně v barvě aplikace */
.bond-purchase__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primaryColor);
}

/* počet kusů: samostatná čtvercová tlačítka − / + a pole mezi nimi */
.bond-purchase__stepper {
  display: flex;
  gap: .5rem;
}

.bond-purchase__stepper button,
.bond-purchase__stepper input {
  height: 3rem;
  border: 1px solid #ced4da;
  border-radius: .375rem;
  background: #fff;
  font-size: 1.25rem;
}

.bond-purchase__stepper button {
  width: 3rem;
}

.bond-purchase__stepper button:disabled {
  opacity: .5;
}

.bond-purchase__stepper input {
  width: 6rem;
  text-align: center;
}
</style>
